import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  ErrorComponent,
  LinearProgressBar,
  Modal,
  MultiSelect,
  Select,
  TableWithHeader,
  Textfield,
} from "../../../components/core";
import { ContentContainer } from "../../../components/layout";
import {
  AGENTS_WITH_FILTER,
  BRANCHES,
  PARTNERS,
} from "../../../constants/graphql/partner-agent-queries";
import {
  PARTNER_CATEGORY,
  SEARCH_PARTNERS_FOR_TABLE,
} from "../../../constants/graphql/partners-query";
import { useApolloClient, useLazyQuery, useQuery } from "@apollo/client";
import { InputText } from "primereact/inputtext";
import PrimereactDateRange from "../../../components/core/date-range-primereact";
import { formatDateYYYYMMDD } from "../../../algorithms/date";
import NewDataTable from "../../../components/new-core/data-table";
import { Column } from "primereact/column";
import AgentTable from "./components/agent-table";
import { Dropdown } from "primereact/dropdown";
import { Tag } from "primereact/tag";
import { getStatusWithSuspendedCheck } from "../../../utils/agent-helpers";
import { Card } from "primereact/card";
import { formatCurrencyValue, formatDate } from "../../../utils/formatters";
import TotalRecords from "../../../components/core/total-records";
import { Strings } from "../../../constants/strings";
import { maxDate } from "../drop-off-status";

const initialFormData = {
  firstName: "",
  lastName: "",
  agentMobile: "",
  verifiedStatus: [],
  agentStatus: -2,
  withApp: -2,
  signupDateFrom: maxDate,
  signupDateInto: new Date(),
  partnerCategoryId: -1,
  partnerId: -2,
  partnerBranch: [],
};

export default function Agents() {
  const client = useApolloClient();
  const operatorId = localStorage.getItem("user")
    ? JSON.parse(localStorage.getItem("user")).operator_id
    : "";

  // Local States
  const [formData, setFormData] = useState(initialFormData);
  const [agentResultList, setAgentResultList] = useState([]);
  const [headerValue, setHeaderValue] = useState("");
  const [allDateFilter, setAllDateFilter] = useState(true);
  const [csvData, setCsvData] = useState([]);
  const [isClicked, setIsClicked] = useState(false);

  // validators
  const [validation, setValidation] = useState({
    icon: `${process.env.PUBLIC_URL}/assets/images/error.png`,
    title: "",
    message: "",
    open: false,
  });

  // GraphQL Queries
  const [getAgents, { error, loading, data, refetch }] =
    useLazyQuery(AGENTS_WITH_FILTER);

  const { data: partnerCategoryData } = useQuery(PARTNER_CATEGORY);

  const { data: allPartnersData } = useQuery(SEARCH_PARTNERS_FOR_TABLE, {
    variables: {
      operatorId,
      partnerCategoryId:
        formData.partnerCategoryId === -2 ? 0 : formData.partnerCategoryId,
    },
    skip: formData.partnerCategoryId === -1,
  });

  const { data: allPartnersNoCategoryData } = useQuery(PARTNERS, {
    variables: { operatorId },
    skip: formData.partnerCategoryId !== -1,
  });

  const getBranches = useQuery(BRANCHES, {
    variables: {
      operatorId,
      partnerId: formData.partnerId !== -1 ? formData.partnerId : undefined,
    },
    skip: formData.partnerId === -1 || formData.partnerId === -2,
  });

  const getAllBranches = useQuery(BRANCHES, {
    variables: { operatorId },
  });

  const headerClassName = "bg-primary-primaryYellow text-secondary-purpleGray";

  // ─── Derived: allStatusSelected ──────────────────────────────────────────
  const allStatusSelected = useMemo(() => {
    const selected = formData.verifiedStatus;
    if (selected.length === 0) return false;
    if (selected.some((item) => item.value === -1)) return true;
    return selected.length === 5; // total verification status options
  }, [formData.verifiedStatus]);

  // ─── Derived: allBranchesSelected ────────────────────────────────────────
  const allBranchesSelected = useMemo(() => {
    const selected = formData.partnerBranch;
    const totalBranches =
      getBranches.data?.partnerBranches?.length ??
      getAllBranches.data?.partnerBranches?.length ??
      0;
    if (selected.length === 0 || totalBranches === 0) return false;
    if (selected.some((item) => item.value === 0)) return true;
    return selected.length === totalBranches;
  }, [formData.partnerBranch, getBranches.data, getAllBranches.data]);

  // ─── Computed option lists ────────────────────────────────────────────────
  const verificationStatusOptions = useMemo(() => {
    const base = [
      { value: 4, label: "1 Step Away" },
      { value: 2, label: "Pending verification" },
      { value: 1, label: "Verified" },
      { value: 3, label: "Rejected" },
      { value: 0, label: "Unverified" },
    ];
    if (!allStatusSelected) {
      base.unshift({ value: -1, label: "-- All Verification Status --" });
    }
    return base;
  }, [allStatusSelected]);

  const partnerCategoryOptions = useMemo(() => {
    if (!partnerCategoryData?.partnerCategories) return [];
    return partnerCategoryData.partnerCategories.map((c) => ({
      value: c.partnerCategoryNo === 0 ? -2 : c.partnerCategoryNo,
      name: c.partnerCategory,
    }));
  }, [partnerCategoryData]);

  const partnerList = useMemo(() => {
    if (formData.partnerCategoryId !== -1 && allPartnersData?.searchCouriers) {
      return allPartnersData.searchCouriers
        .map((p) => ({ value: p.partnerId, name: p.companyNickname }))
        .sort((a, b) => a.name.localeCompare(b.name));
    }
    if (
      formData.partnerCategoryId === -1 &&
      allPartnersNoCategoryData?.partners
    ) {
      return allPartnersNoCategoryData.partners
        .map((p) => ({ value: p.partnerId, name: p.companyNickname }))
        .sort((a, b) => a.name.localeCompare(b.name));
    }
    return [];
  }, [formData.partnerCategoryId, allPartnersData, allPartnersNoCategoryData]);

  const branchList = useMemo(() => {
    const source =
      formData.partnerId !== -1 && formData.partnerId !== -2
        ? getBranches.data?.partnerBranches
        : getAllBranches.data?.partnerBranches;
    if (!source) return [];
    const branches = source.map((b) => ({
      value: b.partnerBranchId,
      label: b.partnerBranch,
    }));
    if (!allBranchesSelected) {
      branches.unshift({ value: 0, label: "-- All Branches --" });
    }
    return branches;
  }, [
    formData.partnerId,
    getBranches.data,
    getAllBranches.data,
    allBranchesSelected,
  ]);

  // Initialize on mount
  useEffect(() => {
    setFormData(initialFormData);
    setAllDateFilter(true);
  }, []);

  // Event handlers
  const handleChange = (e) => {
    const { name, value, maxLength } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const getAgentStatusLabel = (status, comments) => {
    if (status === 1) return "Active";
    if (status === -1 && comments?.toLowerCase().includes("suspended"))
      return "Suspended";
    if (status === -1) return "Blocked";
    return "Unknown";
  };

  const handleFilterChange = (e, field) => {
    const value = e.target ? e.target.value : e;
    setIsClicked(false);

    // Validate date range
    if (field === "signupDateFrom" || field === "signupDateInto") {
      const dateFrom =
        field === "signupDateFrom" ? value : formData.signupDateFrom;
      const dateInto =
        field === "signupDateInto" ? value : formData.signupDateInto;
      if (dateFrom && dateInto) {
        const fromDate = new Date(dateFrom);
        const intoDate = new Date(dateInto);
        if (intoDate < fromDate) {
          setValidation({
            icon: `${process.env.PUBLIC_URL}/assets/images/error.png`,
            title: Strings.popup_error_title,
            message:
              "Sign Up Date (To) cannot be earlier than Sign Up Date (From)",
            open: true,
            actions: (
              <div className="w-full">
                <Button
                  onClick={() => setValidation({ open: false })}
                  label="OK"
                  className="p-2 w-full bg-primary-primaryYellow border-0 text-secondary-purpleGray"
                />
              </div>
            ),
          });
          return;
        }
      }
    }

    // When user picks "All Verification Status", expand to all options
    if (field === "verifiedStatus") {
      let newValue = value;
      const hasAll =
        Array.isArray(value) && value.some((item) => item.value === -1);
      if (hasAll) {
        newValue = [
          { value: 4, label: "1 Step Away" },
          { value: 2, label: "Pending verification" },
          { value: 1, label: "Verified" },
          { value: 3, label: "Rejected" },
          { value: 0, label: "Unverified" },
        ];
      }
      setFormData((prev) => ({ ...prev, verifiedStatus: newValue }));
      return;
    }

    // When user picks "All Branches", expand to all branch options
    if (field === "partnerBranch") {
      let newValue = value;
      const hasAll =
        Array.isArray(value) && value.some((item) => item.value === 0);
      if (hasAll) {
        const source =
          formData.partnerId !== -1 && formData.partnerId !== -2
            ? getBranches.data?.partnerBranches
            : getAllBranches.data?.partnerBranches;
        newValue = (source ?? []).map((b) => ({
          value: b.partnerBranchId,
          label: b.partnerBranch,
        }));
      }
      setFormData((prev) => ({ ...prev, partnerBranch: newValue }));
      return;
    }

    // Reset partnerId and partnerBranch when partner category changes
    if (field === "partnerCategoryId") {
      setFormData((prev) => ({
        ...prev,
        partnerCategoryId: value,
        partnerId: -2,
        partnerBranch: [],
      }));
      return;
    }

    // Reset partnerBranch when partner changes
    if (field === "partnerId") {
      setFormData((prev) => ({
        ...prev,
        partnerId: value,
        partnerBranch: [],
      }));
      return;
    }

    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const buildVariables = () => {
    let updatedVars = { operatorId };

    if (
      formData.partnerCategoryId !== -1 &&
      formData.partnerCategoryId !== -2
    ) {
      updatedVars.partnerCategoryId = formData.partnerCategoryId;
    }

    if (formData.partnerId !== -2 && formData.partnerId !== -1) {
      updatedVars.partnerId = formData.partnerId;
    }

    if (formData.partnerBranch && formData.partnerBranch.length > 0) {
      updatedVars.partnerBranchIds = formData.partnerBranch.map((b) => b.value);
    }

    if (formData.lastName !== "") {
      updatedVars.lastName = formData.lastName;
    }

    if (formData.firstName !== "") {
      updatedVars.firstName = formData.firstName;
    }

    if (formData.agentMobile !== "") {
      updatedVars.agentMobile = formData.agentMobile;
    }

    if (formData.verifiedStatus.length > 0) {
      updatedVars.verifiedStatus = formData.verifiedStatus.map((s) => s.value);
    }

    if (formData.signupDateFrom) {
      updatedVars.signupDateFrom = formatDateYYYYMMDD(formData.signupDateFrom);
    }

    if (formData.signupDateInto) {
      updatedVars.signupDateInto = formatDateYYYYMMDD(formData.signupDateInto);
    }

    return updatedVars;
  };

  const validateFields = () => {
    if (
      formData.partnerCategoryId !== -1 &&
      (formData.partnerId === -1 || formData.partnerId === -2)
    ) {
      setValidation({
        icon: `${process.env.PUBLIC_URL}/assets/images/error.png`,
        title: Strings.popup_error_title,
        message: "Please select a Partner when a Partner Category is selected.",
        open: true,
        actions: (
          <div className="w-full">
            <Button
              onClick={() =>
                setValidation((prev) => ({ ...prev, open: false }))
              }
              label="OK"
              className="p-2 w-full bg-primary-primaryYellow border-0 text-secondary-purpleGray"
            />
          </div>
        ),
      });
      return false;
    }

    if (
      formData.partnerId !== -1 &&
      formData.partnerId !== -2 &&
      formData.partnerBranch.length === 0
    ) {
      setValidation({
        icon: `${process.env.PUBLIC_URL}/assets/images/error.png`,
        title: Strings.popup_error_title,
        message:
          "Please select at least one Branch when a Partner is selected.",
        open: true,
        actions: (
          <div className="w-full">
            <Button
              onClick={() =>
                setValidation((prev) => ({ ...prev, open: false }))
              }
              label="OK"
              className="p-2 w-full bg-primary-primaryYellow border-0 text-secondary-purpleGray"
            />
          </div>
        ),
      });
      return false;
    }

    return true;
  };

  const handleSubmit = () => {
    if (!validateFields()) return;

    setAgentResultList([]);
    setIsClicked(false);
    const filteredObject = buildVariables();

    getAgents({ variables: { ...filteredObject, operatorId } }).then(
      (result) => {
        if (result.data) {
          const processedData = result.data.agentsWithFilter.map((element) => {
            // Helper function to trim and check if value is empty
            const cleanValue = (value) => {
              if (!value || typeof value !== "string") return "";
              return value.trim();
            };

            // Build agent name with fallback to "-"
            const firstName = cleanValue(element.firstName);
            const lastName = cleanValue(element.lastName);
            const suffixName = cleanValue(element.suffixName);

            const agentNameParts = [firstName, lastName, suffixName].filter(
              Boolean,
            );
            const agentName =
              agentNameParts.length > 0 ? agentNameParts.join(" ") : "-";

            return {
              agentMobile: element.agentMobile,
              agentStatus: element.agentStatus,
              agentStatusLabel: getAgentStatusLabel(
                element.agentStatus,
                element.comments,
              ),
              formattedAgentName: `${element.lastName}, ${element.firstName} ${element.middleName || ""}`,

              agentName: agentName,
              loadBalance: element.loadBalance,
              locker: element.locker || "No Locker",
              partnerAgentId: element.partnerAgentId,
              partnerBranch: element.partnerBranch.partnerBranch,
              signupDate: element.signupDate,
              verifiedStatus: element.verifiedStatus,
              createdAt: element.createdAt,
              updatedAt: element.updatedAt,
              comments: element.comments,
              walletBalance: element.walletBalance,
              rejectedIdPics: element.rejectedIdPics,
              lastDropoffLocker: element.lastDropoffLocker,
              lastDropoffDate: element.lastDropoffDate,
              hasIntegration: element.affiliationStatus === "Affiliated",
              partnerCompanyName: element.partnerCompanyName ?? null,
            };
          });

          setAgentResultList(processedData);
          setHeaderValue(generateHeaderValue(filteredObject));
          setIsClicked(true);
        }
      },
    );
  };
  const verificationStatusFilterTemplate = (options) => {
    return (
      <Dropdown
        value={options.value ?? null}
        options={[
          { value: "Unknown", label: "Unknown" },
          { value: "Blocked", label: "Blocked" },
          { value: "Suspended", label: "Suspended" },
          { value: "Active", label: "Active" },
        ]}
        onChange={(e) => {
          const value = e.target.value;
          options.filterCallback(value === "" || value === null ? null : value);
        }}
        placeholder="Select a Status"
        className="p-column-filter"
        showClear
      />
    );
  };

  const generateHeaderValue = (filteredObject) => {
    let title = "Search result for ";

    if (filteredObject.firstName || filteredObject.lastName) {
      title +=
        `agent ${filteredObject.firstName || ""} ${filteredObject.lastName || ""}`.trim();
    } else {
      title += "ALL agents";
    }

    if (
      filteredObject.verifiedStatus &&
      filteredObject.verifiedStatus.length > 0
    ) {
      const idStatusMap = {
        0: "Unverified",
        1: "Verified",
        2: "Pending Verification",
        3: "Rejected",
        4: "1 Step Away",
      };
      const labels = filteredObject.verifiedStatus.map(
        (s) => idStatusMap[s] || s,
      );
      title += ` | ID Status: ${labels.join(", ")}`;
    } else {
      title += " | ALL ID statuses";
    }

    if (filteredObject.agentMobile) {
      title += ` | Mobile: +63${filteredObject.agentMobile}`;
    }

    const partner = partnerList.find(
      (p) => p.value === filteredObject.partnerId,
    );
    if (partner) {
      title += ` | Partner: ${partner.name.toUpperCase()}`;
    } else {
      title += " | ALL partners";
    }

    if (
      filteredObject.partnerBranchIds &&
      filteredObject.partnerBranchIds.length > 0
    ) {
      const branchNames = filteredObject.partnerBranchIds
        .map((id) => {
          const found = branchList.find((b) => b.value === id);
          return found ? found.label.toUpperCase() : id;
        })
        .join(", ");
      title += ` | Branch: ${branchNames}`;
    } else {
      title += " | ALL branches";
    }

    if (filteredObject.signupDateFrom) {
      title += ` | From ${filteredObject.signupDateFrom}`;
    }
    if (filteredObject.signupDateInto) {
      title += ` To ${filteredObject.signupDateInto}`;
    }

    return title;
  };

  const handleReset = () => {
    setFormData(initialFormData);
    setAllDateFilter(true);
    setIsClicked(false);
  };

  // Update CSV when agent results change
  useEffect(() => {
    let csvTemp = [
      [
        "Agent Name",
        "Branch",
        "Agent Status",
        "Verified Status",
        "Signup Date",
        "Mobile Number",
        "Locker Name",
        "Balance",
      ],
    ];

    agentResultList.forEach((element) => {
      csvTemp.push([
        element.agentName,
        element.partnerBranch,
        element.agentStatus,
        element.verifiedStatus,
        element.signupDate,
        element.agentMobile,
        element.locker,
        element.loadBalance,
      ]);
    });

    setCsvData(csvTemp);
  }, [agentResultList]);

  const tableHeader = (
    <div className=" bg-primary-primaryYellow py-2 m-0 items-center">
      <span className="text-lg font-semibold">{headerValue}</span>
    </div>
  );

  return (
    <ContentContainer containerTitle={"Agents"}>
      <div className="grid grid-cols-12 md:grid-cols-6">
        <div className="col-span-12 md:col-span-6">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-2">
            {/* Name Field */}
            <div className="col-span-1 md:col-span-12">
              <div className="grid grid-cols-1 md:grid-cols-12 gap-2">
                <div className="col-span-1 md:col-span-3">
                  <p className="text-base">Name:</p>
                </div>
                <div className="col-span-1 md:col-span-5">
                  <div className="grid grid-cols-2 gap-2">
                    <div className="col-span-1">
                      <Textfield
                        type="text"
                        name="lastName"
                        value={formData.lastName}
                        onChange={(e) => {
                          handleFilterChange(e, "lastName");
                        }}
                        placeholder="Surname"
                      />
                    </div>
                    <div className="col-span-1">
                      <Textfield
                        type="text"
                        name="firstName"
                        value={formData.firstName}
                        onChange={(e) => {
                          handleFilterChange(e, "firstName");
                        }}
                        placeholder="First name"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Mobile Number */}
            <div className="col-span-1 md:col-span-12">
              <div className="grid grid-cols-1 md:grid-cols-12 gap-2">
                <div className="col-span-1 md:col-span-3">
                  <p className="text-base">Registered mobile number:</p>
                </div>
                <div className="col-span-1 md:col-span-5">
                  <div className="flex gap-1">
                    <span className="flex items-center">+63</span>
                    <InputText
                      keyfilter="int"
                      maxLength={10}
                      placeholder="9XXXXXXXXX"
                      className="flex-1 h-10 border-2 focus:outline-yellow-400 rounded-md p-2"
                      name="agentMobile"
                      value={formData.agentMobile}
                      onChange={(e) => {
                        handleFilterChange(e, "agentMobile");
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Verified Status */}
            <div className="col-span-1 md:col-span-12">
              <div className="grid grid-cols-1 md:grid-cols-12 gap-2">
                <div className="col-span-1 md:col-span-3">
                  <p className="text-base">Verified Status:</p>
                </div>
                <div className="col-span-1 md:col-span-5">
                  <MultiSelect
                    value={formData.verifiedStatus}
                    items={verificationStatusOptions}
                    defaultOption={{ value: -2, name: "-- All --" }}
                    onChange={(e) => {
                      handleFilterChange(e, "verifiedStatus");
                    }}
                    // onChange={handleChange}
                    name="verifiedStatus"
                  />
                </div>
              </div>
            </div>

            {/* Agent Status */}
            {/* <div className="col-span-1 md:col-span-12">
              <div className="grid grid-cols-1 md:grid-cols-12 gap-2">
                <div className="col-span-1 md:col-span-3">
                  <p className="text-base">Agent Status:</p>
                </div>
                <div className="col-span-1 md:col-span-5">
                  <Select
                    value={formData.agentStatus}
                    items={[
                      { value: -1, name: "Blocked" },
                      { value: 0, name: "Suspended" },
                      { value: 1, name: "Active" },
                    ]}
                    defaultOption={{ value: -2, name: "-- All --" }}
                    onChange={handleChange}
                    name="agentStatus"
                  />
                </div>
              </div>
            </div> */}

            {/* With App */}
            <div className="col-span-1 md:col-span-12">
              <div className="grid grid-cols-1 md:grid-cols-12 gap-2">
                <div className="col-span-1 md:col-span-3">
                  <p className="text-base">With App:</p>
                </div>
                <div className="col-span-1 md:col-span-5">
                  <Select
                    value={formData.withApp}
                    items={[
                      { value: 1, name: "Yes" },
                      { value: 0, name: "No" },
                    ]}
                    defaultOption={{ value: -2, name: "-- All --" }}
                    onChange={(e) => {
                      handleFilterChange(e, "withApp");
                    }}
                    name="withApp"
                  />
                </div>
              </div>
            </div>

            {/* Signup Date */}
            <div className="col-span-1 md:col-span-12">
              <div className="grid grid-cols-1 md:grid-cols-12 gap-2">
                <div className="col-span-1 md:col-span-3">
                  <p className="text-base">Registration Date:</p>
                </div>
                <div className="col-span-1 md:col-span-5">
                  <PrimereactDateRange
                    // disabled={allDateFilter}
                    onChange={handleFilterChange}
                    startDate={formData.signupDateFrom}
                    endDate={formData.signupDateInto}
                    readOnlyInput={true}
                    max={new Date()}
                    min={new Date("2021-01-01")}
                  />
                </div>
              </div>
            </div>

            {/* Partner Category */}
            <div className="col-span-1 md:col-span-12">
              <div className="grid grid-cols-1 md:grid-cols-12 gap-2">
                <div className="col-span-1 md:col-span-3">
                  <p className="text-base">Partner Category:</p>
                </div>
                <div className="col-span-1 md:col-span-5">
                  <Select
                    value={formData.partnerCategoryId}
                    items={partnerCategoryOptions}
                    defaultOption={{
                      value: -1,
                      name: "-- All Partner Categories --",
                    }}
                    onChange={(e) => {
                      handleFilterChange(e, "partnerCategoryId");
                    }}
                    name="partnerCategoryId"
                  />
                </div>
              </div>
            </div>

            {/* Partner */}
            <div className="col-span-1 md:col-span-12">
              <div className="grid grid-cols-1 md:grid-cols-12 gap-2">
                <div className="col-span-1 md:col-span-3">
                  <p className="text-base">Partner:</p>
                </div>
                <div className="col-span-1 md:col-span-5">
                  <Select
                    value={formData.partnerId}
                    items={partnerList}
                    defaultOption={{
                      value: -2,
                      name:
                        formData.partnerCategoryId === -1
                          ? "-- All Partners --"
                          : "-- Select a Partner --",
                    }}
                    disabled={formData.partnerCategoryId === -1 ? false : false}
                    onChange={(e) => {
                      handleFilterChange(e, "partnerId");
                    }}
                    name="partnerId"
                  />
                </div>
              </div>
            </div>

            {/* Branch */}
            <div className="col-span-1 md:col-span-12">
              <div className="grid grid-cols-1 md:grid-cols-12 gap-2">
                <div className="col-span-1 md:col-span-3">
                  <p className="text-base">Branch:</p>
                </div>
                <div className="col-span-1 md:col-span-5">
                  <MultiSelect
                    value={formData.partnerBranch}
                    items={branchList}
                    defaultOption={{ value: 0, label: "-- All Branches --" }}
                    onChange={(e) => {
                      handleFilterChange(e, "partnerBranch");
                    }}
                    name="partnerBranch"
                    disabled={
                      formData.partnerId === -1 || formData.partnerId === -2
                    }
                    placeholder={
                      formData.partnerId === -1 || formData.partnerId === -2
                        ? "-- All Branches --"
                        : "-- Select a Branch --"
                    }
                  />
                </div>
              </div>
            </div>

            {/* Buttons */}
            <div className="col-span-1 md:col-span-12">
              <div className="grid grid-cols-4 gap-4">
                <Button
                  onClick={handleSubmit}
                  disabled={loading}
                  className="col-span-3 md:col-span-1 h-10"
                  value="Search"
                />
                <button
                  className="flex items-center gap-2 hover:text-blue-500 cursor-pointer underline"
                  onClick={handleReset}
                >
                  <i className="fas fa-undo"></i>
                  <span>Reset</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Results Table */}
        {isClicked && !loading && (
          <Card className="col-span-12 mt-7">
            <div className="bg-primary-primaryYellow text-secondary-purpleGray py-2 px-4">
              <div className="justify-center items-center text-center">
                {tableHeader}
              </div>
              <TotalRecords count={agentResultList?.length} />
            </div>
            {/* <NewDataTable
              items={agentResultList}
              parentClassName="col-span-12"
              // searchResultHeader={tableHeader}
            >
              <Column
                field="agentStatus"
                header="Agent Status"
                headerClassName={headerClassName}
                alignHeader={"center"}
                className={`text-left p-4 font-bold hover:text-blue-500 cursor-pointer`}
                filter
                filterField="agentStatusLabel"
                showFilterMatchModes={false}
                showFilterMenuOptions={false}
                showAddButton={false}
                filterElement={verificationStatusFilterTemplate}
                filterMatchMode="equals"
                body={(rowData) => {
                  const status = getStatusWithSuspendedCheck(rowData);

                  return (
                    <div className="flex justify-center">
                      <Tag
                        value={status}
                        className={`${
                          status === "Active"
                            ? "bg-green-400"
                            : status === "Suspended"
                              ? "bg-orange-400"
                              : status === "Blocked"
                                ? "bg-red-400"
                                : status === "Inactive"
                                  ? "bg-yellow-400"
                                  : "bg-gray-400"
                        } text-secondary-purpleGray`}
                      />
                    </div>
                  );
                }}
              />
              <Column
                field="partnerBranch"
                header="Branch"
                className="text-center"
                headerClassName={headerClassName}
                alignHeader={"center"}
                body={(rowData) => {
                  return (
                    <div>
                      {rowData.partnerBranch !== " "
                        ? rowData.partnerBranch
                        : "-"}
                    </div>
                  );
                }}
              />
              <Column
                field="agentName"
                header="Agent Name"
                className="text-center text-wrap w-[200px]"
                headerClassName={headerClassName}
                alignHeader={"center"}
                body={(rowData) => {
                  return (
                    <div
                      onClick={() =>
                        window.open(
                          `${process.env.REACT_APP_BASE_ENVIRONMENT}agents/agentProfile/${rowData.partnerAgentId}`,
                          "_blank",
                        )
                      }
                      className="cursor-pointer hover:text-blue-500 hover:underline"
                    >
                      {rowData.agentName !== "" ? rowData.agentName : "-"}
                    </div>
                  );
                }}
              />
              <Column
                header="Agent Mobile"
                field="agentMobile"
                className="text-center"
                headerClassName={headerClassName}
                alignHeader={"center"}
                body={(rowData) => {
                  return (
                    <div
                      onClick={() =>
                        window.open(
                          `${process.env.REACT_APP_BASE_ENVIRONMENT}agents/agentProfile/${rowData.partnerAgentId}`,
                          "_blank",
                        )
                      }
                      className="cursor-pointer hover:text-blue-500 hover:underline"
                    >
                      {rowData.agentMobile !== " " ? rowData.agentMobile : "-"}
                    </div>
                  );
                }}
              />
              <Column
                header="Verified Status"
                sortable
                headerClassName={headerClassName}
                alignHeader={"center"}
                field="verifiedStatus"
                body={(rowData) => {
                  return (
                    <div className="flex justify-center gap-2">
                      <Tag
                        value={
                          rowData.verifiedStatus === 0
                            ? "Unverified"
                            : rowData.verifiedStatus === 1
                              ? "Verified"
                              : rowData.verifiedStatus === 2
                                ? "Pending Verification"
                                : rowData.verifiedStatus === 3
                                  ? "Rejected"
                                  : "Unknown"
                        }
                        className={`${
                          rowData.verifiedStatus === 0
                            ? "bg-gray-300/80"
                            : rowData.verifiedStatus === 1
                              ? "bg-green-400"
                              : rowData.verifiedStatus === 2
                                ? "bg-orange-400"
                                : rowData.verifiedStatus === 3
                                  ? "bg-red-400"
                                  : "bg-gray-500"
                        } text-secondary-purpleGray`}
                      />
                      {true &&
                        (rowData.verifiedStatus === 2 ||
                          rowData.verifiedStatus === 3) &&
                        rowData.rejectedIdPics?.length > 0 && (
                          <Tag
                            value="1 Step Away"
                            className="bg-primary-primaryYellowLight text-secondary-purpleGray"
                          />
                        )}
                    </div>
                  );
                }}
              />
              <Column
                field="signupDate"
                header="Last Submitted On"
                alignHeader={"center"}
                headerClassName={headerClassName}
                className="text-center"
                body={(rowData) => {
                  return <div>{formatDate(rowData.signupDate)}</div>;
                }}
              />

              <Column
                field="createdAt"
                alignHeader={"center"}
                sortable
                header="Registration Date"
                headerClassName={headerClassName}
                className="text-center"
                body={(rowData) => {
                  return <div>{formatDate(rowData.createdAt)}</div>;
                }}
              />

              <Column
                field="walletBalance"
                alignHeader={"center"}
                header="MBOX Wallet Balance"
                headerClassName={headerClassName}
                className="text-center"
                body={(rowData) => {
                  return (
                    <div>{formatCurrencyValue(rowData.walletBalance)}</div>
                  );
                }}
              />
            </NewDataTable> */}

            <AgentTable
              data={agentResultList}
              headerClassName={headerClassName}
              selectable={true}
            />
          </Card>
        )}

        {loading && (
          <LinearProgressBar className="col-span-12" label="" open={loading} />
        )}

        {error && !loading && <ErrorComponent />}

        <Modal
          icon={validation.icon}
          title={validation.title}
          message={validation.message}
          open={validation.open}
          actions={validation.actions}
        />
      </div>
    </ContentContainer>
  );
}
